// Mediabibliotek — visar inloggad användares tidigare uppladdade bilder i en
// grid. Klick på en thumbnail returnerar bilden till föräldern (ImageBlockEditor)
// och stänger modalen. Stöder också direkt upload via knapp i toolbar:en så att
// användaren kan lägga till en ny bild utan att gå tillbaka.

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { createImage, ImageListItem, listMyImages } from 'src/shared/services/imageService';

const PAGE_SIZE = 50;

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (image: ImageListItem) => void;
};

export const MediaLibraryModal = ({ open, onClose, onSelect }: Props) => {
  const { t } = useTranslation(['groupMessage', 'common']);
  const [items, setItems] = useState<ImageListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Laddar en sida och appendar till listan. För första öppningen ersätter vi
  // hela listan istället för att appenda (skip=0 + reset).
  const loadPage = useCallback(
    async (skip: number, reset: boolean) => {
      setLoading(true);
      try {
        const response = await listMyImages(skip, PAGE_SIZE);
        const data = response.data ?? [];
        setItems((prev) => (reset ? data : [...prev, ...data]));
        setHasMore(data.length === PAGE_SIZE);
      } catch {
        toast.warning(t('groupMessage:builder.editors.mediaLibraryLoadFailed') as string);
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  // Återställ listan varje gång modalen öppnas så användaren ser senaste uppladdningarna.
  useEffect(() => {
    if (open) {
      setItems([]);
      setHasMore(false);
      loadPage(0, true);
    }
  }, [open, loadPage]);

  const handleUploadChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const response = await createImage(file);
      const url = response.data?.location;
      if (typeof url !== 'string' || url.length === 0) {
        toast.warning(t('groupMessage:builder.editors.uploadFailed') as string);
        return;
      }
      // Ladda om första sidan så den nya bilden hamnar överst i listan med fullständig metadata.
      await loadPage(0, true);
    } catch {
      toast.warning(t('groupMessage:builder.editors.uploadFailed') as string);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant='h6' sx={{ flex: 1 }}>
          {t('groupMessage:builder.editors.mediaLibrary')}
        </Typography>
        <Button
          variant='outlined'
          size='small'
          startIcon={<CloudUploadIcon />}
          disabled={uploading || loading}
          onClick={() => fileInputRef.current?.click()}
          sx={{ mr: 1 }}
        >
          {uploading ? t('groupMessage:builder.editors.uploading') : t('groupMessage:builder.editors.uploadFile')}
        </Button>
        <IconButton onClick={onClose} edge='end' aria-label={t('common:button.close') as string}>
          <Close />
        </IconButton>
        <input ref={fileInputRef} type='file' accept='image/*' onChange={handleUploadChosen} style={{ display: 'none' }} />
      </DialogTitle>
      <DialogContent dividers>
        {loading && items.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {!loading && items.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant='body2'>{t('groupMessage:builder.editors.mediaLibraryEmpty')}</Typography>
          </Box>
        )}
        {items.length > 0 && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 1.5,
              }}
            >
              {items.map((item) => (
                <Box
                  key={item.imageGuid}
                  role='button'
                  tabIndex={0}
                  onClick={() => {
                    onSelect(item);
                    onClose();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(item);
                      onClose();
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    transition: 'border-color 120ms ease, box-shadow 120ms ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      component='img'
                      src={item.location}
                      alt={item.fileName}
                      loading='lazy'
                      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                  <Box sx={{ p: 0.75 }}>
                    <Typography variant='caption' sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.fileName}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            {hasMore && (
              <Stack direction='row' justifyContent='center' sx={{ mt: 2 }}>
                <Button variant='outlined' size='small' disabled={loading} onClick={() => loadPage(items.length, false)}>
                  {loading ? <CircularProgress size={16} /> : t('groupMessage:builder.editors.mediaLibraryLoadMore')}
                </Button>
              </Stack>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:button.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaLibraryModal;
